import type { GroupJob, Member } from "../cloud/api";
import { diffForwarding, effectiveSync } from "../groups/forwarding";
import { t } from "../i18n";

/** Chain-reprogramming job progress (`GET /groups/{id}/jobs/{job_id}`), one line per step. */
export function JobProgress({ job, positions }: { job: GroupJob | undefined; positions: number[] }) {
  return (
    <section class="job" aria-live="polite">
      <p><strong>{t(`job.state.${job?.state ?? "pending"}`)}</strong></p>
      <ol class="timeline">
        {(job?.steps ?? positions.map((p) => ({ member_position: p, action: "program", state: "pending", detail: null }))).map((st, i) => (
          <li key={`${st.member_position}-${st.action}-${i}`} class={st.state === "done" ? "done" : st.state === "failed" ? "failed" : st.state === "pending" ? "" : "current"}>
            {t("job.step", { position: st.member_position, action: t(`job.action.${st.action}`) === `job.action.${st.action}` ? st.action : t(`job.action.${st.action}`), state: t(`job.step_state.${st.state}`) === `job.step_state.${st.state}` ? st.state : t(`job.step_state.${st.state}`) })}
            {st.detail && <span class="muted"> — {st.detail}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function SyncBadge({ m }: { m: Pick<Member, "forwarding"> }) {
  const sync = effectiveSync(m);
  const cls = sync === "in_sync" ? "ok" : sync === "unverified" ? "warn" : "danger";
  return <span class={`badge ${cls}`}>{t(`sync.${sync}`)}</span>;
}

/** Desired vs read-back forwarding for one member, rule by rule (protocol 1.2 `line.forwarding.state`). */
export function ForwardingTable({ members, positions }: { members: readonly Member[]; positions: readonly number[] }) {
  const shown = members.filter((m) => positions.includes(m.position)).sort((a, b) => a.position - b.position);
  return (
    <div class="fwd">
      {shown.map((m) => {
        const rows = diffForwarding(m.forwarding.desired, m.forwarding.actual);
        const src = m.forwarding.actual?.source;
        return (
          <section key={m.position} aria-labelledby={`fwd-${m.position}`}>
            <h4 id={`fwd-${m.position}`}>
              {t("fwd.member", { position: m.position, number: m.number, role: t(`fwd.role.${m.role}`) })} <SyncBadge m={m} />
            </h4>
            <p class="hint">
              {t("fwd.source", { source: src ? t(`fwd.source.${src}`) : t("fwd.source.none") })}
              {m.forwarding.last_readback_at && <> · {t("fwd.readback_at", { when: new Date(m.forwarding.last_readback_at).toLocaleTimeString() })}</>}
              {m.forwarding.last_error && <> · <span class="danger-text">{m.forwarding.last_error}</span></>}
            </p>
            <div class="scroll-x">
              <table>
                <thead>
                  <tr><th>{t("fwd.col.rule")}</th><th>{t("fwd.col.desired")}</th><th>{t("fwd.col.actual")}</th><th>{t("fwd.col.ok")}</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.reason} class={r.ok ? "" : "mismatch"}>
                      <td>{t(`fwd.rule.${r.reason}`)}</td>
                      <td class="ltr">{r.desired}</td>
                      <td class="ltr">{r.actual}</td>
                      <td>{r.ok ? "✓" : "✗"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
